import { mat4, vec3 } from "gl-matrix";
const DEFAULT_PERSPECTIVE = {
    fov: (45 * Math.PI) / 180,
    near: 0.1,
    far: 1000,
    position: [0, 0, 6],
    target: [0, 0, 0],
    up: [0, 1, 0],
    aspect: 1,
};
const DEFAULT_ORTHOGRAPHIC = {
    left: -1,
    right: 1,
    top: 1,
    bottom: -1,
    near: 0.1,
    far: 1000,
    zoom: 1,
    position: [0, 0, 6],
    target: [0, 0, 0],
    up: [0, 1, 0],
};
export class Camera {
    constructor(synth, type, p, controlTo) {
        this.synth = synth;
        const props = { ...(type === "perspective" ? DEFAULT_PERSPECTIVE : DEFAULT_ORTHOGRAPHIC), ...p };
        if (type === "perspective")
            props.aspect ??= synth.width / synth.height;
        this.initialProps = JSON.parse(JSON.stringify(props));
        this.props = props;
        this.viewMatrix = mat4.create();
        this.projectionMatrix = mat4.create();
        this.uniforms = { projectionMatrix: this.projectionMatrix, viewMatrix: this.viewMatrix };
        this._position = props.position;
        this._target = props.target;
        this._up = props.up;
        if (type === "perspective")
            this.perspective(props, controlTo);
        else if (type === "orthographic")
            this.orthographic(props);
    }
    perspective(props, controlTo) {
        this.type = "perspective";
        const { fov, aspect, near, far } = props;
        mat4.perspective(this.projectionMatrix, fov, aspect, near, far);
        mat4.lookAt(this.viewMatrix, this._position, this._target, this._up);
        if (controlTo)
            this.control = new Controls({ element: controlTo, camera: this });
        return this;
    }
    orthographic(props) {
        this.type = "orthographic";
        const { left, right, top, bottom, near, far } = props;
        mat4.ortho(this.projectionMatrix, left, right, top, bottom, near, far);
        mat4.lookAt(this.viewMatrix, this._position, this._target, this._up);
        return this;
    }
    static isPerspective(camera) {
        return camera.type === "perspective";
    }
    updateMatrix() {
        if (Camera.isPerspective(this)) {
            const { fov, aspect, near, far } = this.props;
            mat4.perspective(this.projectionMatrix, fov, aspect, near, far);
            mat4.lookAt(this.viewMatrix, this._position, this._target, this._up);
        }
        else {
            const { left, right, top, bottom, near, far } = this.props;
            mat4.ortho(this.projectionMatrix, left, right, top, bottom, near, far);
            mat4.lookAt(this.viewMatrix, this._position, this._target, this._up);
        }
        return this;
    }
    reset() {
        this._position = [...this.initialProps.position];
        this._target = [...this.initialProps.target];
        this._up = [...this.initialProps.up];
        this.control?.reset();
        return this.updateMatrix();
    }
    resize(width, height) {
        if (Camera.isPerspective(this)) {
            this.props.aspect = width / height;
        }
        else {
            const props = this.props;
            props.left = -width / 2;
            props.right = width / 2;
            props.top = height / 2;
            props.bottom = -height / 2;
            this.props = props;
        }
        this.updateMatrix();
    }
    position(position) {
        this._position[0] = position[0];
        this._position[1] = position[1];
        this._position[2] = position[2];
        if (this.control) {
            this.control.spherical.distance = vec3.distance(this._position, this._target);
            this.control.update();
        }
        return this.updateMatrix();
    }
    target(target) {
        this._target[0] = target[0];
        this._target[1] = target[1];
        this._target[2] = target[2];
        return this.updateMatrix();
    }
    move(position) {
        vec3.add(this._position, this._position, position);
        return this.updateMatrix();
    }
    rotate(target) {
        vec3.add(this._target, this._target, target);
        return this.updateMatrix();
    }
    update() { }
    zoom() {
        return this.control?.spherical.distance;
    }
    screenPanning(enabled = true) {
        if (this.control)
            this.control.screenSpacePanning = enabled;
        return this;
    }
    dispose() {
        this.control?.dispose();
        return this;
    }
    activate() {
        this.control?.activate();
        return this;
    }
}
const DEFAULT_CONTROLS = {
    minDistance: 0.00001,
    maxDistance: 10000,
    sensitivity: { zoom: 0.001, rotate: 0.025 },
    maxPolarAngle: Math.PI,
    screenSpacePanning: false,
};
class Controls {
    constructor(options) {
        if (options.sensitivity && typeof options.sensitivity === "number")
            options.sensitivity = { zoom: options.sensitivity, rotate: options.sensitivity };
        options = { ...DEFAULT_CONTROLS, ...options };
        this.element = options.element;
        this.camera = options.camera;
        this.minDistance = options.minDistance;
        this.maxDistance = options.maxDistance;
        this.sensitivity = options.sensitivity;
        this.maxPolarAngle = options.maxPolarAngle;
        this.screenSpacePanning = options.screenSpacePanning;
        this.dragging = false;
        this.spherical = { distance: this.camera._position[2], polar: Math.PI / 2, azimuth: Math.PI / 2 };
        this.truckDistance = 0;
        this.pedestalDistance = 0;
        this.onDragStart = this.onDragStart.bind(this);
        this.onDragEnd = this.onDragEnd.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onWheel = this.onWheel.bind(this);
        this.activate();
        this.update();
    }
    activate() {
        this.element.addEventListener("wheel", this.onWheel, { passive: false });
        this.element.addEventListener("pointerdown", this.onDragStart, { passive: false });
        this.element.addEventListener("pointermove", this.onMove, { passive: false });
        this.element.addEventListener("pointerup", this.onDragEnd, { passive: false });
    }
    reset() {
        this.spherical = { distance: this.camera._position[2], polar: Math.PI / 2, azimuth: Math.PI / 2 };
        this.update();
    }
    update() {
        const { distance, polar, azimuth } = this.spherical;
        const offsetX = distance * Math.sin(polar) * Math.cos(azimuth);
        const offsetY = distance * Math.cos(polar);
        const offsetZ = distance * Math.sin(polar) * Math.sin(azimuth);
        this.camera._position[0] = this.camera._target[0] + offsetX;
        this.camera._position[1] = this.camera._target[1] + offsetY;
        this.camera._position[2] = this.camera._target[2] + offsetZ;
        this.camera.updateMatrix();
    }
    onWheel(event) {
        this.adjustSphericalCoordinates(0, 0, -event.deltaY * (event.shiftKey ? 5 : 1));
        this.update();
    }
    onDragStart(event) {
        this.dragging = true;
    }
    onMove(event) {
        if (this.dragging) {
            const mx = event.movementX, my = event.movementY;
            if (event.shiftKey)
                this.pan(-mx, my);
            else
                this.adjustSphericalCoordinates(mx, -my, 0);
        }
    }
    onDragEnd(event) {
        this.dragging = false;
    }
    adjustSphericalCoordinates(deltaX, deltaY, deltaZoom) {
        const { rotate, zoom } = this.sensitivity;
        this.spherical.azimuth += deltaX * rotate;
        this.spherical.polar += deltaY * rotate;
        this.spherical.polar = Math.max(0, Math.min(this.maxPolarAngle, this.spherical.polar));
        this.spherical.distance = Math.max(this.minDistance, Math.min(this.maxDistance, this.spherical.distance - deltaZoom * zoom));
        this.update();
    }
    pan(deltaX, deltaY) {
        let panOffset = vec3.create();
        const forward = vec3.create();
        vec3.sub(forward, this.camera._target, this.camera._position);
        vec3.normalize(forward, forward);
        const right = vec3.create();
        vec3.cross(right, forward, this.camera._up);
        vec3.normalize(right, right);
        if (this.screenSpacePanning) {
            const up = vec3.fromValues(0, 1, 0);
            vec3.scale(right, right, deltaX * this.sensitivity.rotate);
            vec3.scale(up, up, deltaY * this.sensitivity.rotate);
            vec3.add(panOffset, right, up);
        }
        else {
            const upLocal = vec3.create();
            vec3.cross(upLocal, right, forward);
            vec3.normalize(upLocal, upLocal);
            vec3.scale(right, right, deltaX * this.sensitivity.rotate);
            vec3.scale(upLocal, upLocal, deltaY * this.sensitivity.rotate);
            vec3.add(panOffset, right, upLocal);
        }
        vec3.add(this.camera._target, this.camera._target, panOffset);
        this.update();
    }
    dispose() {
        this.element.removeEventListener("wheel", this.onWheel);
        this.element.removeEventListener("pointerdown", this.onDragStart);
        this.element.removeEventListener("pointermove", this.onMove);
        this.element.removeEventListener("pointerup", this.onDragEnd);
    }
}
